import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import walkinDriveService from '../../services/walkinDriveService';

const WalkinPanelDashboard = () => {
  const { id: routeDriveId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { isCollapsed } = useSidebar();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [drives, setDrives] = useState([]);
  const [selectedDriveId, setSelectedDriveId] = useState(routeDriveId || '');
  const [currentDrive, setCurrentDrive] = useState(null);
  const [selectedPanel, setSelectedPanel] = useState(1);
  const [queueData, setQueueData] = useState({
    summary: { queued: 0, interviewing: 0, selected: 0, not_selected: 0, total: 0 },
    queue: []
  });
  const [loading, setLoading] = useState(true);
  const [evaluatingApplicant, setEvaluatingApplicant] = useState(null);
  const [evalStatus, setEvalStatus] = useState('interviewing');
  const [evalRating, setEvalRating] = useState(4);
  const [evalNotes, setEvalNotes] = useState('');
  const [evalSubmitting, setEvalSubmitting] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // Load all active drives for selector
  useEffect(() => {
    const fetchDrives = async () => {
      try {
        const res = await walkinDriveService.getDrives({ status: 'active' });
        if (res.success && res.data && res.data.length > 0) {
          setDrives(res.data);
          if (!selectedDriveId) {
            setSelectedDriveId(res.data[0].id);
          }
        }
      } catch (err) {
        console.error('Failed to load drives:', err);
      }
    };
    fetchDrives();
  }, []);

  // When route driveId changes
  useEffect(() => {
    if (routeDriveId) {
      setSelectedDriveId(routeDriveId);
    }
  }, [routeDriveId]);

  // Load current panel queue
  const fetchPanelQueue = async (driveId, panelNum, showLoader = false) => {
    if (!driveId) return;
    try {
      if (showLoader) setLoading(true);
      const res = await walkinDriveService.getPanelQueue(driveId, panelNum);
      if (res.success && res.data) {
        setCurrentDrive(res.data.drive);
        setQueueData({
          summary: res.data.summary || { queued: 0, interviewing: 0, selected: 0, not_selected: 0, total: 0 },
          queue: res.data.queue || []
        });
        setLastRefreshed(new Date());
      }
    } catch (err) {
      console.error('Failed to load panel queue:', err);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDriveId) {
      fetchPanelQueue(selectedDriveId, selectedPanel, true);
    }
  }, [selectedDriveId, selectedPanel]);

  // Auto-refresh interval (every 6 seconds)
  useEffect(() => {
    if (!autoRefresh || !selectedDriveId) return;

    const interval = setInterval(() => {
      fetchPanelQueue(selectedDriveId, selectedPanel, false);
    }, 6000);

    return () => clearInterval(interval);
  }, [autoRefresh, selectedDriveId, selectedPanel]);

  // Open Evaluation Drawer
  const handleOpenEvaluation = (applicant, defaultStatus = 'interviewing') => {
    setEvaluatingApplicant(applicant);
    setEvalStatus(defaultStatus);
    setEvalRating(applicant.interview_rating ? Number(applicant.interview_rating) : 4);
    setEvalNotes(applicant.interview_notes || '');
  };

  // Submit Evaluation
  const handleSaveEvaluation = async (statusOverride = null) => {
    if (!evaluatingApplicant || !selectedDriveId) return;
    setEvalSubmitting(true);

    const finalStatus = statusOverride || evalStatus;

    try {
      const res = await walkinDriveService.updateInterviewStatus(selectedDriveId, evaluatingApplicant.id, {
        status: finalStatus,
        interview_notes: evalNotes,
        interview_rating: evalRating,
        interviewed_by: userProfile?.full_name || user?.email || 'Panel Interviewer'
      });

      if (res.success) {
        setEvaluatingApplicant(null);
        await fetchPanelQueue(selectedDriveId, selectedPanel, false);
      } else {
        alert(res.error || 'Failed to update evaluation');
      }
    } catch (err) {
      alert(err.message || 'Error submitting evaluation');
    } finally {
      setEvalSubmitting(false);
    }
  };

  // Quick preset feedback tags
  const feedbackPresets = [
    'Strong technical proficiency',
    'Good communication & articulation',
    'Solid problem-solving skills',
    'Relevant domain experience',
    'High potential / fast learner',
    'Lacks required hands-on experience',
    'Communication gaps',
    'Expected salary out of budget'
  ];

  const handleAddPreset = (tag) => {
    setEvalNotes(prev => prev ? `${prev}, ${tag}` : tag);
  };

  const getWaitingDuration = (appliedAt) => {
    if (!appliedAt) return '';
    const diffMs = Date.now() - new Date(appliedAt).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours} hr${diffHours > 1 ? 's' : ''} ${diffMins % 60}m ago`;
  };

  const panelCount = currentDrive?.panel_count ? Number(currentDrive.panel_count) : 4;

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        user={userProfile || user}
        notifications={[]}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className={`pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          <BreadcrumbNavigation />

          {/* Top Bar with Drive Selector & Live indicator */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6 pb-4 border-b border-border">
            <div>
              <div className="flex items-center gap-2">
                <Link to="/erp/walkin-drives" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1 mb-1">
                  <Icon name="ArrowLeft" size={14} /> Back to Drives Overview
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name="Users" size={22} />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                    Live Panel Interview Dashboard
                  </h1>
                  <p className="text-xs text-muted-foreground">
                    Real-time shortest-queue candidate dispatch, live evaluation, and instant ATS selection
                  </p>
                </div>
              </div>
            </div>

            {/* Drive Selector Dropdown & Live pulse */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <label className="text-xs font-semibold text-muted-foreground whitespace-nowrap">Drive:</label>
                <select
                  value={selectedDriveId}
                  onChange={(e) => {
                    setSelectedDriveId(e.target.value);
                    navigate(`/erp/walkin-drives/${e.target.value}/panel`);
                  }}
                  className="px-3 py-1.5 rounded-xl border border-border bg-card text-xs font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 shadow-sm"
                >
                  {drives.map(d => (
                    <option key={d.id} value={d.id}>{d.title} ({d.location})</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-card border border-border text-xs shadow-sm">
                <span className="w-2 h-2 rounded-full bg-success animate-ping" />
                <span className="font-semibold text-foreground">Live Sync</span>
                <span className="text-muted-foreground text-[10px]">
                  ({lastRefreshed.toLocaleTimeString()})
                </span>
                <button
                  onClick={() => fetchPanelQueue(selectedDriveId, selectedPanel, true)}
                  title="Manual Refresh"
                  className="ml-1 p-1 rounded hover:bg-muted text-primary"
                >
                  <Icon name="RefreshCw" size={12} />
                </button>
              </div>

              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
                  autoRefresh
                    ? 'bg-primary/10 border-primary/20 text-primary'
                    : 'bg-muted border-border text-muted-foreground'
                }`}
              >
                Auto-refresh: {autoRefresh ? 'ON (6s)' : 'OFF'}
              </button>
            </div>
          </div>

          {/* Panel Selector Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-6 scrollbar-none">
            {Array.from({ length: panelCount }, (_, i) => i + 1).map(pNum => {
              const isSelected = selectedPanel === pNum;
              return (
                <button
                  key={pNum}
                  onClick={() => setSelectedPanel(pNum)}
                  className={`flex items-center gap-2 px-5 py-3 rounded-2xl font-bold text-sm transition-all whitespace-nowrap shadow-sm ${
                    isSelected
                      ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/25 scale-[1.02]'
                      : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon name="UserCheck" size={18} />
                  <span>Panel {pNum}</span>
                  {selectedPanel === pNum && (
                    <span className="ml-1 px-2 py-0.5 rounded-full bg-white/20 text-xs">
                      Active
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Panel Metric Overview */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Waiting in Queue</p>
                  <h3 className="text-2xl font-bold text-blue-500 mt-1">{queueData.summary.queued}</h3>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-500 flex items-center justify-center">
                  <Icon name="Clock" size={20} />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">In Interview</p>
                  <h3 className="text-2xl font-bold text-amber-500 mt-1">{queueData.summary.interviewing}</h3>
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
                  <Icon name="Mic" size={20} />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Selected (In ATS)</p>
                  <h3 className="text-2xl font-bold text-success mt-1">{queueData.summary.selected}</h3>
                </div>
                <div className="w-10 h-10 rounded-xl bg-success/10 text-success flex items-center justify-center">
                  <Icon name="CheckCircle2" size={20} />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Not Selected</p>
                  <h3 className="text-2xl font-bold text-error mt-1">{queueData.summary.not_selected}</h3>
                </div>
                <div className="w-10 h-10 rounded-xl bg-error/10 text-error flex items-center justify-center">
                  <Icon name="XCircle" size={20} />
                </div>
              </div>
            </div>
          </div>

          {/* Live Candidates Queue Table */}
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-foreground">
                  Panel {selectedPanel} Live Candidate Queue
                </h3>
                <p className="text-xs text-muted-foreground">
                  Candidates currently assigned to Panel {selectedPanel} via shortest-queue algorithm
                </p>
              </div>
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs">
                {queueData.queue.length} Candidates Assigned
              </span>
            </div>

            {loading ? (
              <div className="py-16 text-center">
                <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-xs text-muted-foreground">Loading queue for Panel {selectedPanel}...</p>
              </div>
            ) : queueData.queue.length === 0 ? (
              <div className="py-16 text-center">
                <Icon name="Users" size={36} className="mx-auto mb-2 text-muted-foreground opacity-50" />
                <h4 className="font-bold text-foreground text-sm">No Candidates in Panel {selectedPanel} Queue</h4>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
                  When new candidates scan the QR code on-site, the shortest-queue dispatcher will automatically assign them here.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-border bg-muted/40 text-muted-foreground font-semibold">
                      <th className="p-4 w-12 text-center">#</th>
                      <th className="p-4">Candidate Details</th>
                      <th className="p-4">Position Interested</th>
                      <th className="p-4">Queue Status</th>
                      <th className="p-4">Waiting Time</th>
                      <th className="p-4">Interview Evaluation</th>
                      <th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {queueData.queue.map((app, index) => {
                      const isInterviewing = app.status === 'interviewing';
                      const isSelected = app.status === 'selected';
                      const isNotSelected = app.status === 'not_selected';

                      return (
                        <tr
                          key={app.id}
                          className={`transition-colors ${
                            isInterviewing ? 'bg-amber-500/5 hover:bg-amber-500/10' :
                            isSelected ? 'bg-success/5 hover:bg-success/10' :
                            'hover:bg-muted/30'
                          }`}
                        >
                          <td className="p-4 text-center font-bold text-muted-foreground">
                            {index + 1}
                          </td>
                          <td className="p-4">
                            <div className="font-bold text-foreground text-sm flex items-center gap-2">
                              {app.name}
                              {isInterviewing && (
                                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 text-[10px] font-semibold animate-pulse">
                                  Currently In Interview
                                </span>
                              )}
                            </div>
                            <div className="text-muted-foreground text-[11px] mt-0.5">
                              📞 {app.phone} • ✉️ {app.email}
                            </div>
                            {app.resume_url && (
                              <a
                                href={`/${app.resume_url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[11px] text-primary font-semibold hover:underline inline-flex items-center gap-1 mt-1"
                              >
                                <Icon name="FileText" size={12} /> View Resume
                              </a>
                            )}
                          </td>
                          <td className="p-4">
                            <span className="font-semibold text-foreground">{app.position_interested}</span>
                          </td>
                          <td className="p-4">
                            <span className={`px-2.5 py-1 rounded-full font-bold text-[11px] capitalize inline-flex items-center gap-1 ${
                              isSelected ? 'bg-success/15 text-success' :
                              isInterviewing ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400' :
                              isNotSelected ? 'bg-error/15 text-error' :
                              'bg-blue-500/15 text-blue-500'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                isSelected ? 'bg-success' :
                                isInterviewing ? 'bg-amber-500 animate-ping' :
                                isNotSelected ? 'bg-error' :
                                'bg-blue-500'
                              }`} />
                              {app.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="p-4 text-muted-foreground">
                            <div className="font-medium text-foreground">{getWaitingDuration(app.applied_at)}</div>
                            <div className="text-[10px] text-muted-foreground">{app.applied_at}</div>
                          </td>
                          <td className="p-4">
                            {app.interview_rating ? (
                              <div className="flex items-center gap-1 font-bold text-amber-500 mb-0.5">
                                <Icon name="Star" size={13} fill="currentColor" /> {app.interview_rating} / 5
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-[11px] italic">Not rated yet</span>
                            )}
                            {app.interview_notes && (
                              <p className="text-muted-foreground text-[11px] line-clamp-1 italic max-w-xs">
                                "{app.interview_notes}"
                              </p>
                            )}
                          </td>
                          <td className="p-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {app.status === 'queued' && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleOpenEvaluation(app, 'interviewing')}
                                  className="text-xs shadow-sm bg-indigo-600 hover:bg-indigo-700"
                                >
                                  <Icon name="Play" size={12} className="mr-1" /> Start Interview
                                </Button>
                              )}

                              {isInterviewing && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleOpenEvaluation(app, 'selected')}
                                  className="text-xs shadow-sm bg-success hover:bg-success/90"
                                >
                                  <Icon name="CheckCircle" size={12} className="mr-1" /> Complete / Rate
                                </Button>
                              )}

                              {(isSelected || isNotSelected) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleOpenEvaluation(app, app.status)}
                                  className="text-xs"
                                >
                                  <Icon name="Edit" size={12} className="mr-1" /> Edit Evaluation
                                </Button>
                              )}

                              {app.promoted_to_application_id && (
                                <Link
                                  to={`/erp/applicant-tracking-system?applicantId=${app.promoted_to_application_id}`}
                                  className="px-2.5 py-1.5 rounded-xl bg-success/10 text-success text-xs font-bold hover:bg-success/20 transition-colors inline-flex items-center gap-1"
                                >
                                  <Icon name="ExternalLink" size={12} /> ATS Profile
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CANDIDATE EVALUATION DRAWER / MODAL */}
      {evaluatingApplicant && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-2xl w-full p-6 md:p-8 max-h-[90vh] overflow-y-auto animate-fade-in-up">
            <div className="flex items-center justify-between pb-4 mb-6 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-bold text-lg">
                  {evaluatingApplicant.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">{evaluatingApplicant.name}</h2>
                  <p className="text-xs text-muted-foreground">
                    Applied for <strong className="text-foreground">{evaluatingApplicant.position_interested}</strong> • Panel {evaluatingApplicant.panel_number}
                  </p>
                </div>
              </div>
              <button onClick={() => setEvaluatingApplicant(null)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={22} />
              </button>
            </div>

            {/* Candidate Quick Info Card */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 bg-muted/30 rounded-xl border border-border mb-6 text-xs">
              <div>
                <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Phone</span>
                <span className="font-semibold text-foreground">{evaluatingApplicant.phone}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Email</span>
                <span className="font-semibold text-foreground truncate block">{evaluatingApplicant.email}</span>
              </div>
              <div>
                <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Applied Time</span>
                <span className="font-semibold text-foreground">{evaluatingApplicant.applied_at}</span>
              </div>
              {evaluatingApplicant.resume_url && (
                <div className="col-span-full pt-2 border-t border-border flex items-center justify-between">
                  <span className="text-muted-foreground">Candidate Resume Attached</span>
                  <a
                    href={`/${evaluatingApplicant.resume_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary font-bold hover:underline inline-flex items-center gap-1 text-xs"
                  >
                    <Icon name="FileText" size={14} /> Open Resume in New Tab
                  </a>
                </div>
              )}
            </div>

            {/* Evaluation Form */}
            <div className="space-y-5">
              <div>
                <label className="text-xs font-bold text-foreground uppercase tracking-wider block mb-2">
                  Interview Decision / Status *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setEvalStatus('interviewing')}
                    className={`py-3 px-4 rounded-xl text-xs font-bold border transition-all flex flex-col items-center gap-1 ${
                      evalStatus === 'interviewing'
                        ? 'bg-amber-500/15 border-amber-500 text-amber-600 dark:text-amber-400 shadow-sm'
                        : 'bg-muted/20 border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="Mic" size={18} />
                    <span>In Progress</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setEvalStatus('selected')}
                    className={`py-3 px-4 rounded-xl text-xs font-bold border transition-all flex flex-col items-center gap-1 ${
                      evalStatus === 'selected'
                        ? 'bg-success/15 border-success text-success shadow-sm ring-2 ring-success/20'
                        : 'bg-muted/20 border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="CheckCircle2" size={18} />
                    <span>Selected</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setEvalStatus('not_selected')}
                    className={`py-3 px-4 rounded-xl text-xs font-bold border transition-all flex flex-col items-center gap-1 ${
                      evalStatus === 'not_selected'
                        ? 'bg-error/15 border-error text-error shadow-sm'
                        : 'bg-muted/20 border-border text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name="XCircle" size={18} />
                    <span>Not Selected</span>
                  </button>
                </div>
              </div>

              {/* Rating (1 to 5 Stars) */}
              <div>
                <label className="text-xs font-bold text-foreground uppercase tracking-wider block mb-2">
                  Candidate Rating (1 to 5 Stars): <span className="text-amber-500 text-sm font-bold">{evalRating} / 5</span>
                </label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setEvalRating(star)}
                      className={`p-2 rounded-xl transition-all ${
                        evalRating >= star
                          ? 'text-amber-500 bg-amber-500/10 scale-110'
                          : 'text-muted-foreground hover:text-amber-400'
                      }`}
                    >
                      <Icon name="Star" size={24} fill={evalRating >= star ? 'currentColor' : 'none'} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Interview Feedback Presets */}
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1.5">Quick Presets</label>
                <div className="flex flex-wrap gap-1.5">
                  {feedbackPresets.map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleAddPreset(preset)}
                      className="px-2.5 py-1 rounded-lg bg-muted/40 hover:bg-muted text-[11px] text-muted-foreground hover:text-foreground border border-border transition-colors"
                    >
                      + {preset}
                    </button>
                  ))}
                </div>
              </div>

              {/* Detailed Feedback Textarea */}
              <div>
                <label className="text-xs font-bold text-foreground uppercase tracking-wider block mb-1">
                  Interview Notes & Technical Feedback
                </label>
                <textarea
                  rows={4}
                  value={evalNotes}
                  onChange={(e) => setEvalNotes(e.target.value)}
                  placeholder="Enter detailed evaluation, code proficiency, strengths, areas of improvement, compensation alignment..."
                  className="w-full px-3.5 py-2.5 rounded-xl border border-border bg-background text-foreground text-xs focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none leading-relaxed"
                />
              </div>

              {/* Notice when selecting candidate */}
              {evalStatus === 'selected' && (
                <div className="p-3 bg-success/10 border border-success/20 rounded-xl text-success text-xs flex items-start gap-2 animate-fade-in">
                  <Icon name="CheckCircle" size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="block">Automatic ATS Promotion:</strong>
                    Marking this candidate <strong>Selected</strong> will immediately insert them into the core ATS (<code>job_applications</code>) under <strong>application_source = 'walkin_drive'</strong> with their interview score and feedback.
                  </div>
                </div>
              )}

              {evalStatus === 'not_selected' && (
                <div className="p-3 bg-muted/40 border border-border rounded-xl text-muted-foreground text-xs flex items-start gap-2">
                  <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    This candidate will remain saved in walk-in drive history for records, but will <strong>not</strong> enter the active ATS database.
                  </div>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
                <Button variant="outline" onClick={() => setEvaluatingApplicant(null)}>
                  Cancel
                </Button>
                <Button
                  variant="default"
                  disabled={evalSubmitting}
                  onClick={() => handleSaveEvaluation()}
                  className={`font-bold shadow-md ${
                    evalStatus === 'selected' ? 'bg-success hover:bg-success/90' :
                    evalStatus === 'not_selected' ? 'bg-error hover:bg-error/90' : ''
                  }`}
                >
                  {evalSubmitting ? 'Saving...' : (
                    evalStatus === 'selected' ? 'Confirm Selection & Promote to ATS' :
                    evalStatus === 'not_selected' ? 'Mark Candidate Not Selected' :
                    'Update Status'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalkinPanelDashboard;
